import { useState } from 'react'
import {
  useSalesReport,
  useInventoryReport,
  useCollectionsReport,
} from '@hooks/useBatchTracking'

export function ReportsPage() {
  const [reportType, setReportType] = useState<'sales' | 'inventory' | 'collections'>('sales')
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  )
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split('T')[0]
  )

  const { data: salesData, isLoading: salesLoading } = useSalesReport(
    reportType === 'sales' ? startDate : undefined,
    reportType === 'sales' ? endDate : undefined
  )
  const { data: inventoryData, isLoading: inventoryLoading } = useInventoryReport()
  const { data: collectionsData, isLoading: collectionsLoading } = useCollectionsReport(
    reportType === 'collections' ? startDate : undefined,
    reportType === 'collections' ? endDate : undefined
  )

  const isLoading =
    reportType === 'sales'
      ? salesLoading
      : reportType === 'inventory'
      ? inventoryLoading
      : collectionsLoading

  const data =
    reportType === 'sales'
      ? salesData?.data
      : reportType === 'inventory'
      ? inventoryData?.data
      : collectionsData?.data

  const exportCSV = () => {
    if (!data) return

    let csv = ''
    let filename = `${reportType}-report-${new Date().getTime()}.csv`

    if (reportType === 'sales') {
      csv = 'Sales Report\n'
      csv += `Period: ${startDate} to ${endDate}\n\n`
      csv += 'Summary,Value\n'
      csv += `Total Orders,${data.summary?.totalOrders || 0}\n`
      csv += `Total Revenue,₹${(data.summary?.totalRevenue || 0).toLocaleString()}\n`
      csv += `Average Order Value,₹${(data.summary?.averageOrderValue || 0).toLocaleString()}\n`
    } else if (reportType === 'inventory') {
      csv = 'Inventory Report\n\n'
      csv += 'Summary,Count\n'
      csv += `Total Bulk Batches,${data.summary?.totalBulkBatches || 0}\n`
      csv += `Total Packed Goods,${data.summary?.totalPackedGoods || 0}\n`
      csv += `Total Repacking Jobs,${data.summary?.totalRepackingJobs || 0}\n`
      csv += `Active Repacking Jobs,${data.summary?.activeRepackingJobs || 0}\n`
    } else {
      csv = 'Collections Report\n'
      csv += `Period: ${startDate} to ${endDate}\n\n`
      csv += 'Summary,Amount\n'
      csv += `Total Due,₹${(data.summary?.totalDue || 0).toLocaleString()}\n`
      csv += `Total Paid,₹${(data.summary?.totalPaid || 0).toLocaleString()}\n`
      csv += `Total Outstanding,₹${(data.summary?.totalOutstanding || 0).toLocaleString()}\n`
      csv += `Collection Rate,${(data.summary?.collectionRate || 0).toFixed(1)}%\n`
    }

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports & Exports</h1>
        <p className="text-gray-600 text-sm mt-1">
          Generate and export operational reports
        </p>
      </div>

      <div className="card">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type
            </label>
            <div className="flex gap-2">
              {['sales', 'inventory', 'collections'].map((type) => (
                <button
                  key={type}
                  onClick={() =>
                    setReportType(type as 'sales' | 'inventory' | 'collections')
                  }
                  className={`px-4 py-2 rounded font-medium text-sm ${
                    reportType === type
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {(reportType === 'sales' || reportType === 'collections') && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading report...</div>
      ) : (
        <>
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">
                {reportType === 'sales'
                  ? 'Sales Summary'
                  : reportType === 'inventory'
                  ? 'Inventory Summary'
                  : 'Collections Summary'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {reportType === 'sales' && data?.summary && (
                <>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded border-l-4 border-blue-500">
                    <p className="text-xs text-blue-600 font-semibold uppercase">
                      Total Orders
                    </p>
                    <p className="text-2xl font-bold text-blue-900 mt-1">
                      {data.summary.totalOrders}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded border-l-4 border-green-500">
                    <p className="text-xs text-green-600 font-semibold uppercase">
                      Total Revenue
                    </p>
                    <p className="text-2xl font-bold text-green-900 mt-1">
                      ₹{(data.summary.totalRevenue || 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded border-l-4 border-purple-500">
                    <p className="text-xs text-purple-600 font-semibold uppercase">
                      Avg Order Value
                    </p>
                    <p className="text-2xl font-bold text-purple-900 mt-1">
                      ₹{(data.summary.averageOrderValue || 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded border-l-4 border-orange-500">
                    <p className="text-xs text-orange-600 font-semibold uppercase">
                      Invoices
                    </p>
                    <p className="text-2xl font-bold text-orange-900 mt-1">
                      {data.summary.totalInvoices}
                    </p>
                  </div>
                </>
              )}

              {reportType === 'inventory' && data?.summary && (
                <>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded border-l-4 border-blue-500">
                    <p className="text-xs text-blue-600 font-semibold uppercase">
                      Bulk Batches
                    </p>
                    <p className="text-2xl font-bold text-blue-900 mt-1">
                      {data.summary.totalBulkBatches}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded border-l-4 border-green-500">
                    <p className="text-xs text-green-600 font-semibold uppercase">
                      Packed Goods
                    </p>
                    <p className="text-2xl font-bold text-green-900 mt-1">
                      {data.summary.totalPackedGoods.toLocaleString()}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded border-l-4 border-purple-500">
                    <p className="text-xs text-purple-600 font-semibold uppercase">
                      Total Jobs
                    </p>
                    <p className="text-2xl font-bold text-purple-900 mt-1">
                      {data.summary.totalRepackingJobs}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded border-l-4 border-orange-500">
                    <p className="text-xs text-orange-600 font-semibold uppercase">
                      Active Jobs
                    </p>
                    <p className="text-2xl font-bold text-orange-900 mt-1">
                      {data.summary.activeRepackingJobs}
                    </p>
                  </div>
                </>
              )}

              {reportType === 'collections' && data?.summary && (
                <>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded border-l-4 border-blue-500">
                    <p className="text-xs text-blue-600 font-semibold uppercase">
                      Total Due
                    </p>
                    <p className="text-2xl font-bold text-blue-900 mt-1">
                      ₹{(data.summary.totalDue || 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded border-l-4 border-green-500">
                    <p className="text-xs text-green-600 font-semibold uppercase">
                      Total Paid
                    </p>
                    <p className="text-2xl font-bold text-green-900 mt-1">
                      ₹{(data.summary.totalPaid || 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded border-l-4 border-purple-500">
                    <p className="text-xs text-purple-600 font-semibold uppercase">
                      Outstanding
                    </p>
                    <p className="text-2xl font-bold text-purple-900 mt-1">
                      ₹{(data.summary.totalOutstanding || 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded border-l-4 border-orange-500">
                    <p className="text-xs text-orange-600 font-semibold uppercase">
                      Collection %
                    </p>
                    <p className="text-2xl font-bold text-orange-900 mt-1">
                      {(data.summary.collectionRate || 0).toFixed(1)}%
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={exportCSV}
              className="btn-primary"
            >
              📥 Export as CSV
            </button>
          </div>
        </>
      )}
    </div>
  )
}
